// useAuth Hook - Authentication state and actions

import { useCallback, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import {
  login,
  logout,
  refreshToken,
  clearError,
  initializeAuth,
} from "../../../app/slices/authSlice";
import type { LoginCredentials } from "../../../app/slices/authSlice";

/**
 * Hook for authentication state and actions
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, isLoading, login, logout } = useAuth();
 *
 * if (!isAuthenticated) {
 *   return <LoginPage />;
 * }
 *
 * return <button onClick={logout}>Logout</button>;
 * ```
 */
export function useAuth() {
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);

  const { user, token, isAuthenticated, isLoading, isInitialized, error } =
    authState ?? {
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      // No auth slice mounted at all -- treat the check as settled so a guard
      // renders its redirect rather than hanging on a blank screen forever.
      isInitialized: true,
      error: null,
    };

  /**
   * Initialize auth state from localStorage on mount
   */
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      void dispatch(initializeAuth());
    }
    // Only run once on mount - dispatch is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Login with email and password
   */
  const handleLogin = useCallback(
    async (credentials: LoginCredentials) => {
      return dispatch(login(credentials));
    },
    [dispatch],
  );

  /**
   * Logout current user
   */
  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  /**
   * Refresh the authentication token
   */
  const handleRefreshToken = useCallback(async () => {
    return dispatch(refreshToken());
  }, [dispatch]);

  /**
   * Clear any authentication errors
   */
  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    // State
    user,
    token,
    isAuthenticated,
    isLoading,
    isInitialized,
    error,
    // Actions
    login: handleLogin,
    logout: handleLogout,
    refreshToken: handleRefreshToken,
    clearError: handleClearError,
  };
}

export default useAuth;
